import { convertChannelCasing } from 'lib-shared-api-utils';
import { Conversation, MetaConversation } from 'lib-shared-legacy-types';
import moment from 'moment-timezone';
import { ReactElement } from 'react';

import { Highlight } from 'components/highlight';
import { channelAttributes } from 'utils/channels';
import { durationConverter } from 'utils/duration';

import { getConversationTitle } from './conversationTitle';
import { numberFormatterInternational } from './telephonyHelpers';

export default class ConversationSummaryHelper {
  metaConversation: MetaConversation;

  constructor(metaConversation: MetaConversation) {
    this.metaConversation = metaConversation;
  }

  // Main functions needed to build ConversationSummary
  getConversationInfo(): Pick<
    Conversation,
    | 'initial_channel'
    | 'id'
    | 'status'
    | 'created_at'
    | 'closed_at'
    | 'pending_expiration_at'
    | 'direction'
  > {
    const { conversation } = this.metaConversation;
    const {
      initial_channel,
      id,
      status,
      created_at,
      closed_at,
      pending_expiration_at,
      direction,
    } = conversation;

    return {
      initial_channel,
      id,
      status,
      created_at,
      closed_at,
      pending_expiration_at,
      direction,
    };
  }

  getLabels(): {
    title: string;
    requester: string | null;
    queue: string;
    agent: string;
    channel: string;
    status: string;
    statusTitle: string;
  } {
    let title = getConversationTitle(this.metaConversation) as string;
    if (this.metaConversation.conversation.is_abandoned) {
      title = `Abandoned: ${title}`;
    }

    return {
      title,
      requester: this.getRequster(),
      queue: this.getQueueName(),
      agent: this.getAssignedAgent(),
      channel: this.getConversationType(),
      status: this.getStatus(),
      statusTitle: this.getStatusTitle(),
    };
  }

  getInfoListLines(): {
    key: string;
    value: string | ReactElement | null | undefined;
  }[] {
    const conversationId = this.metaConversation.conversation.id || '';
    const subject = this.getSubject();

    return [
      { key: 'Conversation id', value: `#${conversationId}` },
      { key: 'Channel', value: this.getConversationType() },
      { key: 'Direction', value: this.getDirection() },
      { key: 'Queue', value: this.getQueueName() },
      { key: 'Status', value: this.getStatus() },
      { key: 'Created at', value: this.getCreatedAt() },
      { key: 'Closed at', value: this.getClosedAt() },
      {
        key: 'Subject',
        value: subject && <Highlight highlightedValue={subject} />,
      },
      { key: 'Sent to', value: this.getSentTo() },
      { key: 'Sent from', value: this.getSentFrom() },
      {
        key:
          this.metaConversation.conversation.initial_channel === 'sms'
            ? 'Text to'
            : 'Call to',
        value: this.getCalledNumber(),
      },
      {
        key:
          this.metaConversation.conversation.initial_channel === 'sms'
            ? 'Texted from'
            : 'Calling from',
        value: this.getCallingNumber(),
      },

      { key: 'Facebook page', value: this.getFacebookPage() },
      { key: 'Instagram account', value: this.getInstagramAccount() },
      { key: 'Twitter account', value: this.getTwitterAccount() },
      { key: 'Duration', value: this.getDuration() },
    ].filter((item) => !!item.value);
  }

  // Mostly "internal" helper functions
  getQueueName(): string {
    return this.metaConversation.conversation &&
      this.metaConversation.conversation.queue &&
      this.metaConversation.conversation.queue.name
      ? this.metaConversation.conversation.queue.name
      : '';
  }

  getAssignedAgent(): string {
    return this.metaConversation.conversation &&
      this.metaConversation.conversation.assignment &&
      this.metaConversation.conversation.assignment.agent &&
      typeof this.metaConversation.conversation.assignment.agent.name ===
        'string'
      ? this.metaConversation.conversation.assignment.agent.name.split(' ')[0]
      : '';
  }

  getRequster(): string | null {
    const { requester } = this.metaConversation;
    return requester
      ? requester.name || requester.email || requester.phone_number || null
      : null;
  }

  getRequestedBy(): string | null | undefined {
    const { conversation } = this.metaConversation;
    return conversation.requested_by;
  }

  getConversationType(): string {
    const channel = this.metaConversation.conversation.initial_channel;

    if (!channel) {
      return '';
    }

    return (
      channelAttributes[convertChannelCasing(channel, 'snake')]?.name ?? ''
    );
  }

  getCreatedAt(): string | null {
    return this.metaConversation.conversation &&
      this.metaConversation.conversation.created_at
      ? moment(this.metaConversation.conversation.created_at).format('lll')
      : null;
  }
  getClosedAt(): string | null {
    return this.metaConversation.conversation &&
      this.metaConversation.conversation.closed_at
      ? moment(this.metaConversation.conversation.closed_at).format('lll')
      : null;
  }

  getDuration(): string | null {
    const { is_closed, created_at, closed_at } =
      this.metaConversation.conversation;

    if (is_closed && created_at && closed_at) {
      const { days, hours, minutes, seconds } =
        durationConverter.splitTimeUnits(
          moment(closed_at).diff(moment(created_at), 'seconds'),
          true,
        );

      const time = [hours, minutes, seconds]
        .map((n) => String(n).padStart(2, '0'))
        .join(':');

      if (days && days > 0) {
        return `${days} ${days === 1 ? 'day' : 'days'} ${time}`;
      }
      return time;
    }

    return null;
  }

  getAttributes(): Record<string, string> {
    return this.metaConversation.conversation.attributes
      ? this.metaConversation.conversation.attributes
      : {};
  }

  getDirection(): Conversation['direction'] {
    return this.metaConversation.conversation.direction;
  }

  getStatusTitle(): 'Closed' | 'Follow-up' | 'Open' | '' {
    const { status } = this.metaConversation.conversation;

    if (status === 'closed') {
      return 'Closed';
    }
    if (status === 'pending' || status === 'awaitingpending') {
      return 'Follow-up';
    }
    if (status === 'open') {
      return 'Open';
    }
    return '';
  }

  getStatus(): string {
    const status = this.getStatusTitle();
    const { is_abandoned: isAbandoned } = this.metaConversation.conversation;

    return status !== 'Follow-up' && isAbandoned
      ? `${status} (abandoned)`
      : status;
  }

  getSubject(): string | undefined {
    const subject = this.getAttributes().subject;
    if (subject && typeof subject === 'string' && subject.trim().length > 0) {
      return subject;
    }
    return undefined;
  }

  getSentTo(): string | undefined {
    const { to_email, forwarded_from_email } = this.getAttributes();
    if (to_email && typeof to_email === 'string') {
      return this.getDirection() === 'inbound' && forwarded_from_email
        ? forwarded_from_email
        : to_email;
    }
    return undefined;
  }

  getSentFrom(): string | undefined {
    const { from_email } = this.getAttributes();
    if (from_email && typeof from_email === 'string') {
      return from_email;
    }
    return undefined;
  }

  formatNumber(number: string): string {
    return numberFormatterInternational({ phoneNumber: number });
  }

  getCalledNumber(): string | undefined {
    const { called_number } = this.getAttributes();
    if (called_number && typeof called_number === 'string') {
      return this.formatNumber(called_number);
    }
    return undefined;
  }

  getCallingNumber(): string | undefined {
    const { calling_number } = this.getAttributes();
    if (calling_number && typeof calling_number === 'string') {
      return this.formatNumber(calling_number);
    }
    return undefined;
  }

  isChannelFacebookMessenger(): boolean {
    return this.getConversationInfo().initial_channel === 'facebookmessenger';
  }

  isChannelWhatsApp(): boolean {
    return this.getConversationInfo().initial_channel === 'whatsapp';
  }

  getFacebookPage(): string {
    return this.getAttributes().facebook_page_name;
  }

  getInstagramAccount(): string {
    return this.getAttributes().instagram_account_username;
  }

  isTwitterChannel(): boolean {
    return this.getConversationInfo().initial_channel === 'twitter';
  }

  getTwitterAccount(): string | undefined {
    if (this.isTwitterChannel()) {
      return this.getAttributes().integration_contact_point_handle;
    }
    return undefined;
  }

  getOutboundEnabled(): boolean | undefined {
    const attributes = this.getAttributes();

    if (!attributes) {
      return undefined;
    }

    // all the attributes are strings, so we need to convert it manually to a boolean.
    switch (attributes.outbound_enabled) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        // returning undefined in this context means that this attribute is not present.
        return undefined;
    }
  }
}
